<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    title: string;
    subtitle?: string;
    logoUrl?: string;
    assistantName?: string;
    assistantAvatarUrl?: string;
    assistantTagline?: string;
    closeLabel: string;
    onclose: () => void;
    onrequesthuman?: () => void;
    requestHumanLabel?: string;
    showRequestHuman?: boolean;
    showSoundToggle?: boolean;
    soundMuted?: boolean;
    onTogglesound?: () => void;
    soundOnLabel?: string;
    soundOffLabel?: string;
    footerLabel?: string;
    footerHref?: string;
    footerLinkAriaLabel?: string;
    children: Snippet;
    footer?: Snippet;
  }

  let {
    title,
    subtitle,
    logoUrl,
    assistantName,
    assistantAvatarUrl,
    assistantTagline,
    closeLabel,
    onclose,
    onrequesthuman,
    requestHumanLabel,
    showRequestHuman = false,
    showSoundToggle = false,
    soundMuted = false,
    onTogglesound,
    soundOnLabel,
    soundOffLabel,
    footerLabel,
    footerHref,
    footerLinkAriaLabel,
    children,
    footer,
  }: Props = $props();

  let panel: HTMLDivElement | undefined = $state();

  // Avatar prefers the assistant photo, then the service logo. Initial (text
  // fallback) is derived from the assistant name first so a service can set
  // only an assistant name and still get a meaningful initial.
  const avatarSrc = $derived(assistantAvatarUrl ?? logoUrl);
  const initialSource = $derived(
    (assistantName && assistantName.trim()) || title.trim(),
  );
  const initial = $derived(initialSource.charAt(0).toUpperCase() || '?');

  // Header title prefers the assistant name when available, otherwise falls
  // back to the branding title passed in by the widget container.
  const headerTitle = $derived(
    assistantName && assistantName.trim().length > 0
      ? assistantName.trim()
      : title,
  );
  const headerSubtitle = $derived(assistantTagline ?? subtitle);

  $effect(() => {
    // Focus the panel for keyboard users on open
    queueMicrotask(() => panel?.focus());
  });

  function handleKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onclose();
      return;
    }

    if (e.key !== 'Tab' || !panel) return;

    // Basic focus trap within the panel
    const focusables = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = panel.getRootNode() instanceof ShadowRoot
      ? (panel.getRootNode() as ShadowRoot).activeElement
      : document.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
</script>

<div
  class="hd-panel"
  role="dialog"
  aria-modal="false"
  aria-label={headerTitle}
  tabindex="-1"
  bind:this={panel}
  onkeydown={handleKey}
>
  <header class="hd-panel__header">
    <div class="hd-panel__title">
      <div class="hd-panel__avatar">
        {#if avatarSrc}
          <img src={avatarSrc} alt="" />
        {:else}
          <span class="hd-panel__avatar-initial">{initial}</span>
        {/if}
        <span class="hd-panel__presence" aria-hidden="true"></span>
      </div>
      <div class="hd-panel__titles">
        <strong class="hd-panel__title-text">{headerTitle}</strong>
        {#if headerSubtitle}
          <span class="hd-panel__subtitle">{headerSubtitle}</span>
        {/if}
      </div>
    </div>
    <div class="hd-panel__actions">
      {#if showSoundToggle && onTogglesound}
        {@const label = soundMuted ? soundOnLabel : soundOffLabel}
        <button
          type="button"
          class="hd-panel__action"
          onclick={onTogglesound}
          aria-label={label}
          aria-pressed={soundMuted}
          title={label}
        >
          {#if soundMuted}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M11 5 6 9H3v6h3l5 4V5Z"
                fill="currentColor"
              />
              <path
                d="m16 9 5 6m0-6-5 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          {:else}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M11 5 6 9H3v6h3l5 4V5Z"
                fill="currentColor"
              />
              <path
                d="M15.5 8.5a5 5 0 0 1 0 7M18.5 6a8 8 0 0 1 0 12"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          {/if}
        </button>
      {/if}
      {#if showRequestHuman && onrequesthuman && requestHumanLabel}
        <button
          type="button"
          class="hd-panel__action"
          onclick={onrequesthuman}
          aria-label={requestHumanLabel}
          title={requestHumanLabel}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.418 0-8 2.686-8 6v1h16v-1c0-3.314-3.582-6-8-6Z"
              fill="currentColor"
            />
          </svg>
        </button>
      {/if}
      <button
        type="button"
        class="hd-panel__action"
        onclick={onclose}
        aria-label={closeLabel}
        title={closeLabel}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 6l12 12M18 6L6 18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
  </header>

  <div class="hd-panel__body">
    {@render children()}
  </div>

  {#if footer}
    <footer class="hd-panel__footer">{@render footer()}</footer>
  {:else if footerLabel}
    <div class="hd-panel__powered">
      {#if footerHref}
        <a
          class="hd-panel__powered-link"
          href={footerHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={footerLinkAriaLabel ?? footerLabel}
        >
          {footerLabel}
        </a>
      {:else}
        {footerLabel}
      {/if}
    </div>
  {/if}
</div>

<style>
  .hd-panel {
    width: 100%;
    /* Prevent the panel from pushing past the viewport on narrow phones. The
       wrapping `.hd-panel-wrap` already clamps at calc(100vw - 32px); this
       second clamp adds a tighter 24px gutter safety rail when the panel is
       used outside of that wrapper. */
    max-width: calc(100vw - 24px);
    height: 600px;
    max-height: min(680px, calc(100vh - 120px));
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    outline: none;
    font-family: inherit;
  }
  .hd-panel__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    min-height: 64px;
    background: var(--hd-primary, #2563eb);
    color: var(--hd-primary-fg, #ffffff);
    flex: 0 0 auto;
  }
  .hd-panel__title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .hd-panel__avatar {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 9999px;
    background: rgb(255 255 255 / 0.15);
    display: grid;
    place-items: center;
    overflow: visible;
    flex: 0 0 auto;
    box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.15);
  }
  .hd-panel__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 9999px;
  }
  .hd-panel__avatar-initial {
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, 'Liberation Mono',
      monospace;
    font-size: 16px;
    font-weight: 600;
    color: var(--hd-primary-fg, #ffffff);
    line-height: 1;
  }
  .hd-panel__presence {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 10px;
    height: 10px;
    border-radius: 9999px;
    background: #22c55e;
    box-shadow: 0 0 0 2px #ffffff;
  }
  .hd-panel__titles {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 1px;
  }
  .hd-panel__title-text {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.25;
  }
  .hd-panel__subtitle {
    font-size: 12px;
    font-weight: 400;
    opacity: 0.85;
    line-height: 1.3;
  }
  .hd-panel__actions {
    display: flex;
    gap: 2px;
    flex: 0 0 auto;
  }
  .hd-panel__action {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    color: inherit;
    background: transparent;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-panel__action:hover {
    background: rgb(255 255 255 / 0.14);
  }
  .hd-panel__action:focus-visible {
    outline: 2px solid rgb(255 255 255 / 0.6);
    outline-offset: 1px;
  }
  .hd-panel__action svg {
    width: 18px;
    height: 18px;
  }
  .hd-panel__body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: var(--hd-surface, #ffffff);
    overflow: hidden;
  }
  .hd-panel__footer {
    border-top: 1px solid var(--hd-border, #e2e8f0);
    padding: 8px 12px;
    font-size: 11px;
    color: var(--hd-fg-muted, #64748b);
    flex: 0 0 auto;
  }
  .hd-panel__powered {
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--hd-surface-muted, #f8fafc);
    border-top: 1px solid var(--hd-border, #e2e8f0);
    font-size: 11px;
    font-weight: 500;
    color: var(--hd-fg-muted, #64748b);
    letter-spacing: 0.01em;
    flex: 0 0 auto;
  }
  .hd-panel__powered-link {
    color: inherit;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    transition: color 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-panel__powered-link:hover {
    color: var(--hd-fg, #0f172a);
  }
  .hd-panel__powered-link:focus-visible {
    outline: 2px solid rgb(var(--hd-primary-rgb, 37, 99, 235) / 0.28);
    outline-offset: 3px;
    border-radius: 6px;
  }
</style>
